/**
 * workflow-config-forms.module.ts
 *
 * Shared module extracting the wizard / v1 configuration-step form components
 * so they can be reused by BOTH services.module and the workflow-builder-v2
 * module (Option A). This avoids a circular dependency: previously these
 * components lived in services.module, which imports IremboWorkflowBuilderV2Module —
 * so the v2 module could not import services.module to reuse them.
 *
 * Declares + exports the office-assignment, notification-configuration and
 * configuration-receipt-name component trees. No forRoot() calls here: the
 * forRoot providers (Formly/Quill/Monaco/TagInput) are registered once by
 * services.module and shared through the injector hierarchy at runtime.
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { NgbModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { UiModule, UtilsModule } from '@irembo-andela/irembogov3-common';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { QuillModule } from 'ngx-quill';
import { MatExpansionModule } from '@angular/material/expansion';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TagInputModule } from 'ngx-chips';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { AngJsoneditorModule } from '@maaxgr/ang-jsoneditor';

import { OfficeAssignmentConfigComponent } from './configuration-steps/office-assignment/office-assignment-config/office-assignment-config.component';
import { WorkflowStateSelectorComponent } from './configuration-steps/workflow-state-selector/workflow-state-selector.component';
import { OfficeAssignmentFormComponent } from './configuration-steps/office-assignment/office-assignment-form/office-assignment-form.component';
import { OfficeAssignmentListComponent } from './configuration-steps/office-assignment/office-assignment-list/office-assignment-list.component';
import { NotificationConfigurationListComponent } from './configuration-steps/notification-configuration/notification-configuration-list/notification-configuration-list.component';
import { NotificationConfigurationComponent } from './configuration-steps/notification-configuration/notification-configuration/notification-configuration.component';
import { NotificationConfigurationFormComponent } from './configuration-steps/notification-configuration/notification-configuration-form/notification-configuration-form.component';
import { NotificationConfigurationPreviewComponent } from './configuration-steps/notification-configuration/notification-configuration-preview/notification-configuration-preview.component';
import { ChooseNotifificationTemplateComponent } from './configuration-steps/notification-configuration/notification-configuration-form/choose-notifification-template/choose-notifification-template.component';
import { ConfigurationReceiptNameComponent } from './configuration-steps/configuration-receipt-name/configuration-receipt-name.component';
import { SafeHtmlPipe } from '../../core/pipes/safe-html.pipe';

@NgModule({
  declarations: [
    SafeHtmlPipe,
    OfficeAssignmentConfigComponent,
    WorkflowStateSelectorComponent,
    OfficeAssignmentFormComponent,
    OfficeAssignmentListComponent,
    NotificationConfigurationListComponent,
    NotificationConfigurationComponent,
    NotificationConfigurationFormComponent,
    NotificationConfigurationPreviewComponent,
    ChooseNotifificationTemplateComponent,
    ConfigurationReceiptNameComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbModule,
    NgbNavModule,
    NgSelectModule,
    UiModule,
    UtilsModule,
    FormlyModule,
    FormlyBootstrapModule,
    QuillModule,
    MatExpansionModule,
    DragDropModule,
    TagInputModule,
    MonacoEditorModule,
    AngJsoneditorModule,
  ],
  exports: [
    // top-level forms reused by the v2 inspector + the wizard
    OfficeAssignmentConfigComponent,
    NotificationConfigurationComponent,
    ConfigurationReceiptNameComponent,
    WorkflowStateSelectorComponent,
    // sub-components services.module templates still reference directly
    OfficeAssignmentFormComponent,
    OfficeAssignmentListComponent,
    NotificationConfigurationListComponent,
    NotificationConfigurationFormComponent,
    NotificationConfigurationPreviewComponent,
    ChooseNotifificationTemplateComponent,
    SafeHtmlPipe,
  ],
})
export class WorkflowConfigFormsModule {}
